/**
 * GlobalFacilitySelector.tsx
 *
 * Searchable, multi-select facility dropdown that lives in the main NavSidebar.
 * When one or more facilities are chosen, useGlobalFacilityStore persists the
 * choice via the reserved /filters/persist namespace and every facility-aware
 * list page silently scopes itself to those facilities (without mutating the
 * page's own saved filter preset).
 *
 * Selection is committed when the dropdown closes (not on each toggle) so that
 * we only fire one query invalidation per editing session.
 *
 * The control is styled for the dark sidebar and is hidden when the sidebar
 * is collapsed to icons.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import * as adminSvc from '@/services/admin.service';

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

export function GlobalFacilitySelector() {
  const selectedIds = useGlobalFacilityStore((s) => s.selectedFacilityIds);
  const setSelected = useGlobalFacilityStore((s) => s.setSelected);

  const { data: facilities = [], isLoading } = useQuery({
    queryKey: ['admin', 'facilities'],
    queryFn: () => adminSvc.listFacilities(),
    staleTime: 5 * 60_000,
  });

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  // Draft selection while the dropdown is open; committed on close.
  const [draft, setDraft] = useState<number[]>(selectedIds);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep the draft in sync whenever the store changes while closed.
  useEffect(() => {
    if (!open) setDraft(selectedIds);
  }, [selectedIds, open]);

  async function commit(ids: number[]) {
    if (sameIds(ids, selectedIds)) return;
    setBusy(true);
    try {
      await setSelected(ids);
    } catch {
      // Revert draft to the persisted value on failure.
      setDraft(selectedIds);
    } finally {
      setBusy(false);
    }
  }

  function closeAndCommit() {
    setOpen(false);
    void commit(draft);
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) closeAndCommit();
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft, selectedIds]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const activeFacilities = useMemo(
    () => facilities.filter((f) => f.active && f.facilityId > 0),
    [facilities],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeFacilities;
    return activeFacilities.filter((f) =>
      f.facilityName.toLowerCase().includes(term),
    );
  }, [activeFacilities, search]);

  const draftSet = useMemo(() => new Set(draft), [draft]);

  const label = useMemo(() => {
    if (isLoading) return 'Loading…';
    if (selectedIds.length === 0) return 'All Facilities';
    if (selectedIds.length === 1) {
      const f = facilities.find((x) => x.facilityId === selectedIds[0]);
      return f ? f.facilityName : '1 facility';
    }
    return `${selectedIds.length} facilities`;
  }, [isLoading, selectedIds, facilities]);

  function toggle(id: number) {
    setDraft((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  }

  function toggleOpen() {
    if (open) {
      closeAndCommit();
    } else {
      setDraft(selectedIds);
      setOpen(true);
    }
  }

  async function clearAll(e: React.SyntheticEvent) {
    e.stopPropagation();
    setDraft([]);
    setOpen(false);
    await commit([]);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={isLoading || busy}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={toggleOpen}
        className={cn(
          'group/facility flex h-8 w-full items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2 text-xs text-white/80 transition-colors',
          'hover:bg-white/10 focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-white/20',
          (isLoading || busy) && 'cursor-not-allowed opacity-70',
          selectedIds.length > 0 && 'border-blue-400/50 bg-blue-500/15 text-white',
        )}
        title={selectedIds.length > 0 ? `Scoped to ${label}` : 'All Facilities'}
      >
        <Building2 className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
        <span className="flex-1 truncate text-left">{label}</span>
        {selectedIds.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear global facility"
            onClick={clearAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void clearAll(e);
              }
            }}
            className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-white/60 hover:bg-white/10 hover:text-white"
          >
            <X className="h-3 w-3" />
          </span>
        )}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 opacity-60 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label="Select facilities"
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-slate-200 bg-white text-slate-800 shadow-xl"
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search facilities…"
                className="h-8 w-full rounded border border-slate-200 bg-white pl-8 pr-2 text-xs outline-none focus:border-blue-400"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            <OptionRow
              label="All Facilities"
              active={draft.length === 0}
              onClick={() => setDraft([])}
            />
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs italic text-slate-400">No facilities match</p>
            )}
            {filtered.map((f) => (
              <OptionRow
                key={f.facilityId}
                label={f.facilityName}
                active={draftSet.has(f.facilityId)}
                onClick={() => toggle(f.facilityId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors',
        active ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-100',
      )}
    >
      <Check
        className={cn('h-3.5 w-3.5 flex-shrink-0', active ? 'opacity-100' : 'opacity-0')}
      />
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}
